import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import { ProdutosLista } from "@/components/ProdutosLista";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ptBR } from "date-fns/locale";
import { CalendarDays } from "lucide-react";

function DataBR({ valor, onChange, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const sel = valor ? new Date(valor + "T12:00:00") : undefined;
  const min = new Date(D3 + "T00:00:00");
  const max = new Date(MAX_DATA + "T23:59:59");
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={testid}
          className="flex-1 flex items-center gap-2 border border-border rounded-md px-3 h-11 text-sm bg-white disabled:opacity-60 text-left capitalize">
          <CalendarDays size={15} className="text-muted-foreground shrink-0" />
          {sel
            ? `${sel.toLocaleDateString("pt-BR")} — ${sel.toLocaleDateString("pt-BR", { weekday: "long" })}`
            : <span className="text-muted-foreground normal-case">Escolher data</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" locale={ptBR} selected={sel} defaultMonth={sel || min}
          disabled={(d) => d < min || d > max || d.getDay() === 0}
          onSelect={(d) => {
            if (!d) return;
            const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            onChange(iso);
            setOpen(false);
          }} />
      </PopoverContent>
    </Popover>
  );
}

const calcD3 = () => {
  const d = new Date(Date.now() + 3 * 86400000);
  while (d.getUTCDay() === 0) d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};
const D3 = calcD3();
const MAX_DATA = new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10);

export default function Atendimento() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pedido, setPedido] = useState(null);
  const [step, setStep] = useState("loading"); // loading, confirmar, confirmado
  const [editaveis, setEditaveis] = useState({ telefone: "", complemento: "", referencia: "" });
  const [datas, setDatas] = useState([D3, "", "", "", ""]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/atendimento/${token}`)
      .then((r) => {
        setPedido(r.data);
        setEditaveis({ telefone: r.data.telefone || "", complemento: "", referencia: "" });
        setStep("confirmar");
      })
      .catch(() => setStep("erro"));
  }, [token]);

  const confirmar = async () => {
    const datasValidas = [...new Set(datas.filter(Boolean))];
    if (datasValidas.length < 5) {
      toast.error("Informe as 5 datas — todas diferentes, a partir da primeira data disponível");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/cliente/confirmar-dados/${pedido.id}`,
        { ...editaveis, datas_disponiveis: datasValidas });
      toast.success(r.data.mensagem || "Confirmado!");
      navigate(`/cliente/servico/${r.data.servico_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  const setData = (i, v) => {
    if (v && new Date(v + "T12:00:00").getDay() === 0) {
      toast.error("Domingos não estão disponíveis para atendimento.");
      return;
    }
    if (v && datas.some((d, j) => j !== i && d === v)) {
      toast.error("Essa data já foi selecionada. Escolha uma data diferente.");
      return;
    }
    setDatas((ds) => ds.map((d, j) => (j === i ? v : d)));
  };

  if (step === "loading") return <div className="min-h-screen flex items-center justify-center">Carregando…</div>;
  if (step === "erro") return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-[#E60000] mx-auto mb-6" />
        <h1 className="font-heading font-extrabold text-2xl">Link inválido</h1>
        <p className="text-muted-foreground mt-2">Este link expirou ou já foi utilizado. Entre em contato com a loja.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-border">
        <div className="max-w-xl mx-auto px-6 py-5 flex items-center gap-3">
          <div className="w-7 h-7 brand-bg" />
          <div>
            <div className="font-heading font-extrabold text-sm">{pedido?.empresa_nome || "MontaJá"}</div>
            <div className="label-cap leading-none mt-1">Montagem profissional</div>
          </div>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-6 py-8">
        <div className="label-cap">Confirmação de pedido</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-2 leading-tight">
          Olá, {pedido?.nome?.split(" ")[0]}!
        </h1>
        <p className="text-sm text-muted-foreground mt-3">
          Recebemos as informações do seu pedido pela <strong>{pedido?.empresa_nome}</strong>.
          Confira os dados e, se precisar, ajuste seu contato e endereço.
        </p>

        <div className="mt-8 space-y-4">
          <ReadOnly label="Produto" v={pedido?.produtos?.length > 1 ? `Pedido com ${pedido.produtos.length} itens` : pedido?.produto} />
          <ProdutosLista produtos={pedido?.produtos} />
          <div className="grid grid-cols-2 gap-3">
            <ReadOnly label="Quantidade" v={pedido?.quantidade} />
            {pedido?.cor && <ReadOnly label="Cor" v={pedido.cor} />}
          </div>
          <ReadOnly label="Endereço" v={`${pedido?.endereco} · ${pedido?.cidade}/${pedido?.estado}`} />
          <ReadOnly label="CPF" v={pedido?.cpf} mono />

          <div className="pt-3 border-t border-border">
            <div className="label-cap mb-3">O que você pode atualizar</div>
            <Editavel label="Telefone / WhatsApp" v={editaveis.telefone} onChange={(v) => setEditaveis(e => ({ ...e, telefone: v }))} testid="aten-tel" />
            <Editavel label="Complemento" v={editaveis.complemento} onChange={(v) => setEditaveis(e => ({ ...e, complemento: v }))} testid="aten-comp" />
            <Editavel label="Ponto de referência" v={editaveis.referencia} onChange={(v) => setEditaveis(e => ({ ...e, referencia: v }))} testid="aten-ref" />
          </div>

          <div className="pt-3 border-t border-border">
            <div className="label-cap mb-1">Suas datas disponíveis *</div>
            <p className="text-xs text-muted-foreground mb-3">
              Informe <strong>5 datas diferentes</strong> em que você pode receber o técnico
              (a partir de {D3.split("-").reverse().join("/")}, dentro de 90 dias, exceto domingos).
              A primeira data já vem preenchida e é obrigatória.
              Um montador da sua região escolherá uma delas e o agendamento será confirmado automaticamente.
            </p>
            <div className="space-y-2">
              {datas.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <DataBR valor={d} disabled={i === 0} testid={`aten-data-${i}`}
                    onChange={(iso) => setData(i, iso)} />
                  {i === 0 && <span className="text-[10px] font-bold uppercase text-muted-foreground whitespace-nowrap">1ª data · obrigatória</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <Button onClick={confirmar} disabled={submitting}
          className="mt-8 w-full btn-sharp brand-bg text-white hover:bg-[#001A5E] h-12"
          data-testid="aten-confirmar">
          {submitting ? "Confirmando…" : "Confirmar e agendar"}
        </Button>
        <div className="mt-3 text-xs text-muted-foreground text-center">
          Após a confirmação, um técnico da sua região aceitará o atendimento em uma das datas que você informou.
        </div>
      </div>
    </div>
  );
}

function ReadOnly({ label, v, mono }) {
  return (
    <div>
      <Label className="label-cap">{label}</Label>
      <div className={`mt-1 px-3 py-2 border border-border bg-[#F4F4F5] text-sm ${mono ? "font-mono" : ""}`}>{v || "—"}</div>
    </div>
  );
}
function Editavel({ label, v, onChange, testid }) {
  return (
    <div className="mt-3">
      <Label className="label-cap">{label}</Label>
      <Input value={v} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testid} />
    </div>
  );
}
